import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Pixels of slack when deciding whether a direction is still scrollable.
 *
 * The track carries 2px of padding so a snapped card's focus ring is not clipped, which leaves
 * `scrollLeft` resting at 2 rather than 0. Without this tolerance the start edge would report
 * itself as scrollable, and the fade would appear on the left before anything had been scrolled.
 */
const SCROLL_EPSILON = 5;

/**
 * Wires up the complementary product ("Add-ons") cards on the PDP.
 *
 * The cards reuse the theme's `product-form-component` for the Ajax add, so this component only
 * owns the two behaviors that plumbing does not cover:
 *
 * 1. Re-enabling the Add buttons. `ProductFormComponent` disables its add to cart button on every
 *    `variant:selected` event reaching the section, without checking which product the event came
 *    from. It re-enables on `variant:update`, but that handler bails out when the event's product
 *    id does not match the form's, which is always the case here. Changing a variant on the main
 *    product would otherwise leave these buttons permanently disabled.
 * 2. Updating a card's displayed price when its own variant select changes.
 *
 * The block renders inside `product-recommendations`, which replaces its own innerHTML once the
 * recommendations resolve. That rebuilds this element, so each instance only ever owns the cards
 * it was rendered with and its listeners follow the connect/disconnect lifecycle.
 *
 * @extends {Component}
 */
class ComplementaryProductsList extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.variantUpdate, this.#enableAddButtons);
    document.addEventListener(ThemeEvents.cartUpdate, this.#enableAddButtons);

    const track = this.#track;

    if (track) {
      track.addEventListener('scroll', this.#updateScrollState, { passive: true });
      this.#resizeObserver = new ResizeObserver(this.#updateScrollState);
      this.#resizeObserver.observe(track);
      this.#updateScrollState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.variantUpdate, this.#enableAddButtons);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#enableAddButtons);

    this.#track?.removeEventListener('scroll', this.#updateScrollState);
    this.#resizeObserver?.disconnect();
  }

  /**
   * The horizontally scrolling card track.
   *
   * @returns {HTMLElement | undefined} The track element, when the block rendered any cards.
   */
  get #track() {
    const track = this.refs.track;

    return track instanceof HTMLElement ? track : undefined;
  }

  /**
   * Scrolls one card towards the start of the track.
   */
  previous() {
    this.#scrollByCard(-1);
  }

  /**
   * Scrolls one card towards the end of the track.
   */
  next() {
    this.#scrollByCard(1);
  }

  /**
   * Scrolls the track by a whole card in the given direction.
   *
   * @param {number} direction - -1 towards the start, 1 towards the end.
   */
  #scrollByCard(direction) {
    const track = this.#track;
    const card = track?.querySelector('.complementary-card');

    if (!track || !(card instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;

    track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  /**
   * Records which directions still have content to scroll to.
   *
   * The edge fade and each arrow are driven off these attributes, so neither appears on a side
   * that is already fully scrolled.
   */
  #updateScrollState = () => {
    const track = this.#track;

    if (!track) return;

    const remaining = track.scrollWidth - track.clientWidth - track.scrollLeft;

    this.dataset.canScrollPrev = String(track.scrollLeft > SCROLL_EPSILON);
    this.dataset.canScrollNext = String(remaining > SCROLL_EPSILON);
  };

  /**
   * Updates the card's price to match the newly selected variant.
   *
   * @param {Event} event - The change event from the card's variant select.
   */
  handleVariantChange(event) {
    const select = event.target;

    if (!(select instanceof HTMLSelectElement)) return;

    const card = select.closest('.complementary-card');
    const price = card?.querySelector('.complementary-card__price');

    if (!(card instanceof HTMLElement) || !price) return;

    const formatted = this.#priceFor(card, select.value);

    if (formatted) price.textContent = formatted;
  }

  /**
   * Reads the formatted price for a variant from the card's price map.
   *
   * @param {HTMLElement} card - The card element holding the `data-variant-prices` map.
   * @param {string} variantId - The variant id to look up.
   * @returns {string | undefined} The formatted price, or undefined when unavailable.
   */
  #priceFor(card, variantId) {
    const raw = card.dataset.variantPrices;

    if (!raw) return undefined;

    try {
      /** @type {Record<string, string>} */
      const prices = JSON.parse(raw);

      return prices[variantId];
    } catch {
      return undefined;
    }
  }

  /**
   * Restores the enabled state of every Add button whose selected variant is purchasable.
   */
  #enableAddButtons = () => {
    for (const container of this.querySelectorAll('add-to-cart-component')) {
      const form = container.closest('form');
      const variantInput = form?.querySelector('[name="id"]');

      const unavailable =
        variantInput instanceof HTMLSelectElement
          ? variantInput.selectedOptions[0]?.disabled === true
          : variantInput instanceof HTMLInputElement && variantInput.disabled;

      if (!('enable' in container) || !('disable' in container)) continue;

      const addToCart = /** @type {{ enable: () => void, disable: () => void }} */ (
        /** @type {unknown} */ (container)
      );

      if (unavailable) {
        addToCart.disable();
      } else {
        addToCart.enable();
      }
    }
  };
}

if (!customElements.get('complementary-products-list')) {
  customElements.define('complementary-products-list', ComplementaryProductsList);
}
